import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { Alert, Button } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { startLeadFinderSearch } from "@app/scene/map/store/leadFinder/actions";
import { getLeadFinderSearch } from "@app/scene/map/store/leadFinder/selectors";
import { getLeadGenPlan, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderLimitAlert from "@app/util/leadFinder/showLeadFinderLimitAlert";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import History from "./components/History";
import SearchArea from "./components/SearchArea";
import SearchBox from "./components/SearchBox";
import styles from "./LeadFinderPopover.module.scss";

interface Props {
  changeQuery?: boolean;
  defaultQuery?: string;
  isOwner: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  query?: string;
  showQueryWarning?: boolean;
  startLeadFinderSearch: typeof startLeadFinderSearch.request;
}

const messages = defineMessages({
  queryWarning: {
    id: "map.tool.leadFinder.popover.queryWarning",
    defaultMessage:
      "Changing the search term above will clear previous results and any selected leads. Make sure to <b>add any desired Leads from the current selection</b> before changing your search.",
    description: "Lead Finder popover query change warning",
  },
  title: {
    id: "map.tool.leadFinder.popover.title",
    defaultMessage: "Lead Finder",
    description: "Lead Finder popover title",
  },
  upgrade: {
    id: "map.tool.leadFinder.popover.upgrade",
    defaultMessage: "Upgrade for 3x results per search",
    description: "Upgrade description",
  },
});

const LeadFinderPopover: React.FC<Props> = ({
  changeQuery,
  defaultQuery,
  isOwner,
  leadGenPlan,
  query,
  showQueryWarning,
  startLeadFinderSearch,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const onSearch = useCallback(
    (value) => {
      startLeadFinderSearch({ search: value });
    },
    [startLeadFinderSearch]
  );

  const handleSearch = useCallback(
    (value) => {
      startLeadFinderSearch({
        onLimitThresholdReached: (limits) => {
          showLeadFinderLimitAlert(intl, limits, isOwner, onSearch, value);
        },
        search: value,
      });
      analytics.completed(["Find Leads button"]);
    },
    [analytics, intl, isOwner, onSearch, startLeadFinderSearch]
  );

  const handleUpgradeClick = useCallback(() => {
    showLeadFinderUpsell(leadGenPlan, intl, isOwner);
  }, [intl, isOwner, leadGenPlan]);

  return (
    <ExtendedAnalytics keyPath="Lead Finder Popover">
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>{intl.formatMessage(messages.title)}</span>
          {leadGenPlan === LeadFinderSubscriptionPlan.BASIC && (
            <Button
              className={styles.upsell}
              leftIcon={<FontAwesomeIcon icon={faCircleInfo} size="xs" />}
              onClick={handleUpgradeClick}
            >
              {intl.formatMessage(messages.upgrade)}
            </Button>
          )}
        </div>
        <SearchBox onSearch={handleSearch} query={changeQuery ? query : defaultQuery} />
        {showQueryWarning && (
          <Alert
            className={styles.alert}
            message={intl.formatMessage(messages.queryWarning, {
              b: (text) => <b>{text}</b>,
            })}
            showIcon
            type="warning"
          />
        )}
        <SearchArea />
        <History />
      </div>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  leadGenPlan: getLeadGenPlan(state),
  query: getLeadFinderSearch(state),
});

const mapDispatchToProps = {
  startLeadFinderSearch: startLeadFinderSearch.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadFinderPopover);
